import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import {
  Button, Form, FormFeedback, FormGroup, FormText, Input, Label,
} from 'reactstrap';
import isEmail from 'validator/es/lib/isEmail';

import PasswordResetRequestMutation from 'src/mutations/PasswordResetRequestMutation';
import FlashesStore from 'src/stores/FlashesStore';

import AuthContainer, { AUTH_TYPE_FORGOT_PASSWORD } from '../AuthContainer';

/**
 * Password reset request form component that allows users to request a password reset email.
 * Validates email input and handles the password reset mutation.
 * @param {object} props - Component props
 * @param {object} props.router - Router object for navigation
 * @returns {React.ReactElement} The AuthForgotPassword component
 */
function AuthForgotPassword({ router }) {
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(null);
  const [processing, setProcessing] = useState(false);

  const intl = useIntl();

  const pageTitle = intl.formatMessage({
    id: 'auth.auth_forgot_password.page.title',
    defaultMessage: 'Enosi - request a password reset email',
  });
  const formTitle = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.title',
    defaultMessage: 'Forgot Password',
  });
  const emailLabel = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.email.label',
    defaultMessage: 'Your account’s email address',
  });
  const emailPlaceholder = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.email.placeholder',
    defaultMessage: 'Your account’s email address',
  });
  const emailFeedbackInvalid = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.email.form_feedback.invalid',
    defaultMessage: 'Email is not valid',
  });
  const emailHelpText = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.email.help_text',
    defaultMessage: 'Please enter the email address associated with your Powertracer account',
  });
  const submitLabel = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.submit.label',
    defaultMessage: 'Send password reset link',
  });
  const msgInprogress = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.state.invalid_still_processing',
    defaultMessage: 'We are still processing your request...',
  });
  const msgInvalidData = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.state.invalid_data',
    defaultMessage: 'Form data not valid. Please see below.',
  });
  const msgSuccess = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.response_message.success',
    defaultMessage: 'Password reset was successful. Check your email shortly.',
  });
  const msgFailure = intl.formatMessage({
    id: 'auth.auth_forgot_password.form.response_message.error',
    defaultMessage: 'Password reset was NOT successful',
  });

  const handleEmailChange = (e) => {
    const { value: emailValue } = e.target;
    const emailValidResult = isEmail(emailValue);

    setEmail(emailValue);
    setEmailValid(emailValidResult);
  };

  const handleSubmitSuccess = (_response) => {
    setProcessing(false);
    FlashesStore.flash(
      FlashesStore.SUCCESS,
      msgSuccess,
      'passwordResetRequest',
    );

    router.push('/login');
  };

  const handleSubmitFailure = (error) => {
    setProcessing(false);
    FlashesStore.flash(
      FlashesStore.ERROR,
      `${msgFailure}: ${error.message}.`,
      'passwordResetRequest',
    );
  };

  const isValid = () => emailValid;

  const handleSubmit = (e) => {
    e.preventDefault();

    const mutationName = 'passwordResetRequest';

    FlashesStore.reset();

    if (processing) {
      FlashesStore.flash(FlashesStore.INFO, msgInprogress, mutationName);
      return;
    }

    if (!isValid()) {
      FlashesStore.flash(FlashesStore.ERROR, msgInvalidData, mutationName);
      return;
    }

    setProcessing(true);

    PasswordResetRequestMutation(
      email,
      handleSubmitSuccess,
      handleSubmitFailure,
    );
  };

  const disabled = email === '' || !emailValid;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <AuthContainer type={AUTH_TYPE_FORGOT_PASSWORD} title={formTitle}>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">{emailLabel}</Label>
            <Input
              type="email"
              id="email"
              aria-describedby="email"
              placeholder={emailPlaceholder}
              onChange={handleEmailChange}
              value={email}
              disabled={processing}
              valid={emailValid}
              invalid={emailValid !== null && !emailValid}
            />
            <FormFeedback>{emailFeedbackInvalid}</FormFeedback>
            <FormText>{emailHelpText}</FormText>
          </FormGroup>
          <Button type="submit" color="primary" disabled={processing || disabled}>
            {submitLabel}
          </Button>
        </Form>
      </AuthContainer>
    </>
  );
}

AuthForgotPassword.propTypes = {
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AuthForgotPassword;
